import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession, useUser } from "@clerk/clerk-react";
import { useUsers } from "shared/contexts/users.context";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { FileLogStatus, FileLog } from "shared/types/file";
import { UniversalTable } from "shared/components/UniversalTable";
import { FILES_TABLE_CONFIG, FILES_COLUMNS_CONFIG } from "./filesTableConfig";
import { createFilesCells } from "./filesCellFactory";
import { ColumnVisibilityToggle } from "shared/components/UniversalTable/components";
import { useColumnVisibility } from "shared/components/UniversalTable/hooks/useColumnVisibility";
import EPCMetadataModal from "../epcMetadataModal/epcMetadataModal";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from "@heroui/react";
import { AlertCircle, Trash } from "assets/icons";
import ButtonNew from "components/buttonNew/buttonNew";
import { toast } from "react-toastify";
import { Chip } from "@heroui/react";
import { CaretDown, CaretUp, CaretLeft, CaretRight, DownloadSimple } from "@phosphor-icons/react";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { downloadFilesZip } from "shared/api/download-files-zip";
import { fetchMatchingFileIds } from "shared/api/fetch-matching-file-ids";
import { startZipDownloadJob } from "shared/api/zip-download-job";

interface FilesOverviewProps {
  incomingBuildingId?: string;
  incomingUnitId?: string;
  incomingFileLabel?: string;
}

const PAGE_SIZE = 50;
// Small selections download instantly (synchronous); larger ones run as a
// background job so the browser doesn't hang and the user gets a notification.
const SYNC_DOWNLOAD_LIMIT = 25;
const MAX_DOWNLOAD_FILES = 500;

const FilesOverview = ({
  incomingBuildingId,
  incomingUnitId,
  incomingFileLabel,
}: FilesOverviewProps) => {
  const routeParams = useParams<{ buildingId?: string; unitId?: string }>();
  const resolvedBuildingId = incomingBuildingId ?? routeParams.buildingId;
  const resolvedUnitId = incomingUnitId ?? routeParams.unitId;

  const [files, setFiles] = useState<FileLog[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { session } = useSession();
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const { users } = useUsers();

  // Filter states for UniversalTable searchConfig
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  const [buildingFilter, setBuildingFilter] = useState<string[]>(resolvedBuildingId ? [resolvedBuildingId] : []);
  const [unitFilter, setUnitFilter] = useState<string[]>(resolvedUnitId ? [resolvedUnitId] : []);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [labelFilter, setLabelFilter] = useState<string[]>(incomingFileLabel ? [incomingFileLabel] : []);
  const [ownershipFilter, setOwnershipFilter] = useState<string[]>([]);

  const [docLabelCounts, setDocLabelCounts] = useState<any[]>([]);
  const [showZeroDocLabels, setShowZeroDocLabels] = useState(false);

  // Column visibility
  const { hiddenColumns, setHiddenColumns } = useColumnVisibility({
    columns: FILES_COLUMNS_CONFIG.columns,
    storageKey: 'files-table-hidden-columns',
  });

  // EPC modal state
  const [isEpcModalOpen, setIsEpcModalOpen] = useState(false);
  const [currentEpcFile, setCurrentEpcFile] = useState<FileLog | null>(null);
  const [currentEpc, setCurrentEpc] = useState<any>(null);

  // Delete modal state
  const { isOpen: deleteModalOpen, onOpen: openDeleteModal, onClose: closeDeleteModal } = useDisclosure();
  const [fileToDelete, setFileToDelete] = useState<FileLog | null>(null);

  // Picker modal state
  const [pickerState, setPickerState] = useState<{
    isOpen: boolean;
    mode: string | null;
    currentValue: any;
    fileIds: number[];
    contextValue: any;
  }>({
    isOpen: false,
    mode: null,
    currentValue: null,
    fileIds: [],
    contextValue: null
  });

  // Sorting state
  const [sortField] = useState<'id' | 'documentDate' | null>(null);
  const [sortDirection] = useState<'asc' | 'desc'>('asc');

  // Selection state (lifted so bulk actions can read it)
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  // Odoo-style "select all matching": when true the bulk actions target the
  // ENTIRE filtered result set across pages, not just the loaded selection.
  const [selectAllMatching, setSelectAllMatching] = useState(false);

  // Builds the shared filter query params used by the file list AND the
  // "select all matching" id resolver, so both filter identically server-side.
  // Send ALL selected buildings/labels (comma-separated) so the backend filters
  // across the full dataset, not just the page currently in view.
  const buildFilterQueryData = useCallback(() => {
    const queryData: Record<string, string> = {};
    if (debouncedSearch) queryData.search = debouncedSearch;
    if (buildingFilter.length > 0) queryData.buildingId = buildingFilter.join(",");
    if (unitFilter.length > 0) queryData.unitId = unitFilter[0];
    if (labelFilter.length > 0) queryData.fileLabel = labelFilter.join(",");
    if (statusFilter.length > 0) queryData.fileLogStatus = statusFilter[0];
    if (ownershipFilter.length > 0) queryData.ownershipSituation = ownershipFilter[0];
    return queryData;
  }, [debouncedSearch, buildingFilter, unitFilter, labelFilter, statusFilter, ownershipFilter]);

  // Resolves the IDs of EVERY document matching the current filters (across all
  // pages), used when "select all matching" is active.
  const fetchAllMatchingFileIds = useCallback(
    () => fetchMatchingFileIds(buildFilterQueryData(), session),
    [buildFilterQueryData, session],
  );

  const handleDownloadSelection = useCallback(async () => {
    if (!session || isDownloadingZip) return;

    setIsDownloadingZip(true);
    const loadingToast = toast.info("Download wordt voorbereid...", { autoClose: false });
    try {
      // Resolve the ids to download: either the entire filtered set or the
      // explicitly selected rows on the current page(s).
      let ids: number[];
      if (selectAllMatching) {
        ids = await fetchAllMatchingFileIds();
      } else {
        ids = Array.from(selectedRows);
      }

      if (ids.length === 0) {
        toast.dismiss(loadingToast);
        toast.error("Geen bestanden geselecteerd");
        return;
      }
      if (ids.length > MAX_DOWNLOAD_FILES) {
        toast.dismiss(loadingToast);
        toast.error(
          `Je kan maximaal ${MAX_DOWNLOAD_FILES} bestanden tegelijk downloaden (${ids.length} geselecteerd). Verfijn je filter.`,
        );
        return;
      }

      // Small selections: download synchronously for an instant result.
      if (ids.length <= SYNC_DOWNLOAD_LIMIT) {
        const { includedCount, skippedCount } = await downloadFilesZip(ids, session);
        toast.dismiss(loadingToast);
        if (skippedCount > 0) {
          toast.warning(
            `${includedCount} ${includedCount === 1 ? "bestand" : "bestanden"} gedownload, ${skippedCount} overgeslagen`,
          );
        } else {
          toast.success(
            `${includedCount} ${includedCount === 1 ? "bestand" : "bestanden"} gedownload`,
          );
        }
        return;
      }

      // Larger selections: run as a background job so the page doesn't hang.
      // The user is notified in-app with a download link when it's ready.
      if (!user?.id) {
        toast.dismiss(loadingToast);
        toast.error("Kon gebruiker niet bepalen — log opnieuw in");
        return;
      }
      await startZipDownloadJob(ids, session, user.id);
      toast.dismiss(loadingToast);
      toast.success(
        `Je download met ${ids.length} bestanden wordt op de achtergrond voorbereid. Je krijgt een melding met downloadlink zodra hij klaar is.`,
      );
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error("Error downloading zip:", error);
      toast.error(error instanceof Error ? error.message : "Download mislukt");
    } finally {
      setIsDownloadingZip(false);
    }
  }, [selectAllMatching, selectedRows, session, isDownloadingZip, fetchAllMatchingFileIds, user?.id]);

  const clearSelection = useCallback(() => {
    setSelectedRows(new Set());
    setSelectAllMatching(false);
  }, []);

  // Debounce search changes
  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
  }, [searchText]);

  // Sync filters when route params change (same component, different URL)
  useEffect(() => {
    if (resolvedBuildingId) setBuildingFilter([resolvedBuildingId]);
  }, [resolvedBuildingId]);
  useEffect(() => {
    if (resolvedUnitId) setUnitFilter([resolvedUnitId]);
  }, [resolvedUnitId]);

  const fetchFiles = useCallback(async (page: number) => {
    setIsLoading(true);
    setFiles([]);

    const queryData: any = {
      page: page.toString(),
      pageSize: PAGE_SIZE.toString(),
      ...(sortField && { sortField, sortDirection }),
      ...buildFilterQueryData(),
    };

    const queryParams = new URLSearchParams(queryData).toString();
    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/get-files?${queryParams}`,
        { method: "GET" },
        session
      );
      const result = await response.json();

      // Backend now returns { data, totalCount }
      const data = result.data ?? result;
      const count = result.totalCount ?? data.length;

      setFiles(data);
      setTotalCount(count);
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      setIsLoading(false);
    }
  }, [sortField, sortDirection, session, buildFilterQueryData]);

  // Fetch on page change or filter/search change
  useEffect(() => {
    fetchFiles(currentPage);
  }, [currentPage, fetchFiles]);

  // Reset to page 1 when filters change (search is handled separately via debounce)
  useEffect(() => {
    setCurrentPage(1);
  }, [buildingFilter, unitFilter, labelFilter, statusFilter, ownershipFilter]);

  // "Select all matching" is tied to a specific filter set + selection, so clear
  // it whenever the filters/search change or the selection is emptied.
  useEffect(() => {
    setSelectAllMatching(false);
  }, [buildingFilter, unitFilter, labelFilter, statusFilter, ownershipFilter, debouncedSearch]);

  useEffect(() => {
    if (selectedRows.size === 0) setSelectAllMatching(false);
  }, [selectedRows]);

  // Refresh event listener
  useEffect(() => {
    const handleRefresh = () => fetchFiles(currentPage);
    window.addEventListener('refreshDocuments', handleRefresh);
    return () => window.removeEventListener('refreshDocuments', handleRefresh);
  }, [fetchFiles, currentPage]);

  // Fetch document label counts when building/unit selection changes
  useEffect(() => {
    async function fetchDocLabelCounts() {
      if (!buildingFilter.length && !unitFilter.length) {
        setDocLabelCounts([]);
        return;
      }

      const queryData: any = {
        // Send ALL selected buildings (comma-separated) so the label counts
        // accumulate across every selected building, server-side.
        buildingId: buildingFilter.length > 0 ? buildingFilter.join(",") : "",
        unitId: unitFilter.length > 0 ? unitFilter[0] : "",
        acquisitionId: "",
        exclusionType: "",
        secondaryLabel: "",
      };

      const queryParams = new URLSearchParams(queryData).toString();

      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/document-label-counts?${queryParams}`,
          { method: "GET" },
          session,
        );
        const data = await response.json();
        setDocLabelCounts(data);
      } catch (error) {
        console.error("Error fetching document label counts:", error);
      }
    }
    fetchDocLabelCounts();
  }, [buildingFilter, unitFilter, session]);

  // Filter update handlers
  const handleFilterChange = useCallback((key: string, values: string[]) => {
    switch (key) {
      case 'building':
        setBuildingFilter(values);
        if (values.length > 0) setUnitFilter([]);
        break;
      case 'unit':
        setUnitFilter(values);
        if (values.length > 0) setBuildingFilter([]);
        break;
      case 'status':
        setStatusFilter(values);
        break;
      case 'ownership':
        setOwnershipFilter(values);
        break;
      case 'label':
        setLabelFilter(values);
        break;
    }
  }, []);

  const filterSections = useMemo(() => [
    {
      key: 'building',
      label: 'Gebouw',
      options: [],
      color: 'neutral' as const,
      fetchFromAPI: {
        endpoint: '/building/all-ids',
        transform: (buildingIds: string[]) =>
          buildingIds.map(id => ({ key: id, label: id })),
        cacheDuration: 5 * 60 * 1000,
      },
      extractOptionsFromData: (rows: FileLog[]) => {
        const buildings = Array.from(new Set(
          rows.map(file => file.building?.id).filter(Boolean)
        )).sort();
        return buildings.map(id => ({ key: id!, label: id! }));
      }
    },
    {
      key: 'unit',
      label: 'Unit',
      options: [],
      color: 'neutral' as const,
      fetchFromAPI: {
        endpoint: '/units/all-ids',
        transform: (unitIds: string[]) =>
          unitIds.map(id => ({ key: id, label: id })),
        cacheDuration: 5 * 60 * 1000,
      },
      extractOptionsFromData: (rows: FileLog[]) => {
        const units = Array.from(new Set(
          rows.map(file => file.unit?.id).filter(Boolean)
        )).sort();
        return units.map(id => ({ key: id!, label: id! }));
      }
    },
    {
      key: 'status',
      label: 'Status',
      options: Object.values(FileLogStatus).map((value) => ({
        key: value,
        label: value
      })),
      color: 'moss' as const,
    },
    {
      key: 'ownership',
      label: 'Eigendomssituatie',
      options: [
        { key: 'in_eigendom', label: 'In eigendom' },
        { key: 'in_due_diligence', label: 'In Due Diligence' },
        { key: 'in_prospectie', label: 'In prospectie' }
      ],
      color: 'brick' as const,
    },
    {
      key: 'label',
      label: 'Document Label',
      options: [],
      color: 'neutral' as const,
      // Fetch the COMPLETE set of document labels from the backend (not just the
      // labels present in the currently loaded page). This mirrors how the building
      // filter loads all building IDs, and keeps filtering fully server-side.
      fetchFromAPI: {
        endpoint: '/file/file-labels',
        transform: (labels: any[]) => {
          const names = Array.from(new Set(
            (labels || [])
              .filter((label) => label?.documentType !== 'picture')
              .map((label) => label?.label)
              .filter(Boolean)
          )).sort();
          return names.map((label) => ({ key: label, label }));
        },
        cacheDuration: 5 * 60 * 1000,
      },
    }
  ], []);

  // Handle file update
  const handleUpdateFile = useCallback(async (fileId: number, updates: any) => {
    try {
      await fetchWithAuth(
        `${API_URL}/file/update-file-log/${fileId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updates),
        },
        session
      );

      setFiles(prev => prev.map(file =>
        file.id === fileId ? { ...file, ...updates } : file
      ));

      toast.success("Bestand bijgewerkt");
    } catch (error) {
      console.error("Error updating file:", error);
      toast.error("Fout bij bijwerken van bestand");
    }
  }, [session]);

  // Handle file deletion
  const handleDeleteFile = useCallback((file: FileLog) => {
    setFileToDelete(file);
    openDeleteModal();
  }, [openDeleteModal]);

  const confirmDelete = async () => {
    if (!fileToDelete) return;

    try {
      const res = await fetchWithAuth(
        `${API_URL}/file`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fileToDelete),
        },
        session
      );
      if (res.status === 200) {
        setFiles(prev => prev.filter(file => file.id !== fileToDelete.id));
        setTotalCount(prev => prev - 1);
        toast.success("Bestand werd verwijderd!");
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Fout bij verwijderen van bestand");
    } finally {
      closeDeleteModal();
      setFileToDelete(null);
    }
  };

  // Handle EPC modal
  const handleOpenEpcModal = async (file: FileLog) => {
    setCurrentEpcFile(file);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/file-log-with-epc/${file.id}`,
        { method: "GET" },
        session
      );
      if (response.ok) {
        const fileLogWithEpc = await response.json();
        setCurrentEpc(fileLogWithEpc.epc);
      } else {
        console.error('Failed to fetch EPC data:', response.status);
      }
    } catch (error) {
      console.error('Error fetching EPC data:', error);
    }
    setIsEpcModalOpen(true);
  };

  const handleEpcSaved = async () => {
    await fetchFiles(currentPage);

    if (currentEpcFile) {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/file/file-log-with-epc/${currentEpcFile.id}`,
          { method: "GET" },
          session
        );

        if (response.ok) {
          const fileLogWithEpc = await response.json();
          setCurrentEpc(fileLogWithEpc.epc || null);
        }
      } catch (error) {
        console.error('Error refreshing EPC data:', error);
      }
    }
  };

  const getEpcType = (): 'unit' | 'building' => {
    if (currentEpcFile?.label?.includes('EPC gemeenschap')) return 'building';
    return 'unit';
  };

  const [editingFileId, setEditingFileId] = useState<number | null>(null);

  const handlePickerEvent = useCallback((event: { mode: string; fileIds: number[]; currentValue: any }) => {
    const fieldMapping = FILES_TABLE_CONFIG.pickerConfig?.fieldMappings.find(
      mapping => mapping.eventMode === event.mode
    );

    let contextValue = null;
    if (fieldMapping?.extractContextValue && event.fileIds.length > 0) {
      const firstFile = files.find(f => f.id === event.fileIds[0]);
      if (firstFile) {
        contextValue = fieldMapping.extractContextValue(firstFile);
      }
    }

    setPickerState({
      isOpen: true,
      mode: event.mode,
      currentValue: event.currentValue,
      fileIds: event.fileIds,
      contextValue
    });
  }, [files]);

  const handlePickerClose = useCallback(() => {
    setPickerState({
      isOpen: false,
      mode: null,
      currentValue: null,
      fileIds: [],
      contextValue: null
    });
  }, []);

  const handlePickerConfirm = useCallback(async (value: any) => {
    if (!session || pickerState.fileIds.length === 0 || !pickerState.mode) return;

    const fileIds = pickerState.fileIds;
    const fieldMapping = FILES_TABLE_CONFIG.pickerConfig?.fieldMappings.find(
      mapping => mapping.eventMode === pickerState.mode
    );

    if (!fieldMapping) {
      toast.error("Onbekend veld");
      return;
    }

    const fieldLabel = FILES_TABLE_CONFIG.pickerConfig?.fieldLabels?.[fieldMapping.fieldName] || fieldMapping.fieldName;
    const payload = fieldMapping.buildUpdatePayload?.(value, pickerState.currentValue);

    try {
      const updatePromises = fileIds.map(async (fileId) => {
        const endpoint = FILES_TABLE_CONFIG.pickerConfig?.apiUpdateEndpoint?.replace('{id}', String(fileId));
        const response = await fetchWithAuth(
          `${API_URL}${endpoint}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to update file ${fileId}`);
        }

        setFiles(prev => prev.map(file =>
          file.id === fileId ? { ...file, ...payload } : file
        ));
      });

      await Promise.all(updatePromises);

      toast.success(`${fieldLabel} bijgewerkt voor ${fileIds.length} ${fileIds.length === 1 ? 'bestand' : 'bestanden'}`);
      handlePickerClose();
    } catch (error) {
      console.error("Error updating file field:", error);
      toast.error(error instanceof Error ? error.message : "Fout bij bijwerken van veld");
    }
  }, [session, pickerState, setFiles, handlePickerClose]);

  const renderFileRow = useCallback((file: FileLog, context: any) => {
    const enhancedContext = {
      ...context,
      onPickerEvent: handlePickerEvent
    };

    const cells = createFilesCells({
      file,
      isEditing: editingFileId === file.id,
      handlers: {
        onDelete: handleDeleteFile,
        onOpenEpcModal: handleOpenEpcModal,
        onEdit: (file) => {
          setEditingFileId(file.id as number);
        },
        onSave: async (fileId, updates) => {
          await handleUpdateFile(fileId, updates);
          setEditingFileId(null);
        },
        onCancel: () => {
          setEditingFileId(null);
        }
      },
      users: users || [],
      context: enhancedContext
    });

    return cells;
  }, [handleDeleteFile, handleOpenEpcModal, handleUpdateFile, users, editingFileId, handlePickerEvent]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Documenten</title>
        </Helmet>
      </HelmetProvider>

      <div className="z-10 mb-10 flex flex-col gap-4">
        {/* Select-all-matching banner (Odoo-style cross-pagination selection) */}
        {selectAllMatching ? (
          <div className="flex flex-wrap items-center justify-center gap-3 rounded-lg border border-moss-primary/30 bg-moss-primary/10 p-3 text-sm text-neutral-primary">
            <span>
              Alle <strong>{totalCount}</strong>{" "}
              {totalCount === 1 ? "document is" : "documenten zijn"} geselecteerd.
            </span>
            <button
              onClick={clearSelection}
              className="font-medium text-moss-primary underline-offset-2 hover:underline"
            >
              Selectie wissen
            </button>
          </div>
        ) : files.length > 0 && selectedRows.size >= files.length && totalCount > selectedRows.size ? (
          <div className="flex flex-wrap items-center justify-center gap-3 rounded-lg border border-moss-primary/20 bg-moss-primary/5 p-3 text-sm text-neutral-primary">
            <span>
              <strong>{selectedRows.size}</strong>{" "}
              {selectedRows.size === 1 ? "document" : "documenten"} op deze pagina geselecteerd.
            </span>
            <button
              onClick={() => setSelectAllMatching(true)}
              className="font-medium text-moss-primary underline-offset-2 hover:underline"
            >
              Selecteer alle {totalCount} documenten
            </button>
          </div>
        ) : null}

        {/* Document Label Counts */}
        {(buildingFilter.length > 0 || unitFilter.length > 0) && docLabelCounts.length > 0 && (
          <div className="bg-white rounded-lg p-4 border border-neutral-primary/10 shadow-sm">
            <h3 className="text-sm font-medium text-neutral-primary mb-3">Beschikbare labels</h3>
            <div className="flex flex-wrap gap-2">
              {docLabelCounts
                .filter(({ count }: any) => (showZeroDocLabels ? true : count > 0))
                .sort((a: any, b: any) => a.label.localeCompare(b.label))
                .map(({ label, count, resourceType }: any) => {
                  const isUnitLabel = resourceType === 'unit';
                  const isBuildingLabel = resourceType === 'building';
                  const isActive = labelFilter.includes(label);

                  return (
                    <Chip
                      key={`${label}-${resourceType}`}
                      onClick={() => {
                        if (isActive) {
                          setLabelFilter((prev) => prev.filter((l) => l !== label));
                        } else {
                          setLabelFilter((prev) => [...prev, label]);
                        }
                      }}
                      className={`cursor-pointer transition-all hover:scale-105 ${
                        isActive
                          ? "ring-2 ring-moss-primary ring-offset-2"
                          : ""
                      } ${
                        count > 0
                          ? isUnitLabel
                            ? "bg-moss-primary text-white hover:bg-moss-primary/90"
                            : isBuildingLabel
                            ? "bg-brick-secondary-fine text-neutral-primary hover:bg-brick-secondary-fine/80 border border-neutral-primary/20"
                            : "bg-neutral-primary text-white hover:bg-neutral-primary/90"
                          : "bg-neutral-secondary-mid/20 text-neutral-secondary-mid border border-neutral-secondary-mid/30 hover:bg-neutral-secondary-mid/30"
                      }`}
                    >
                      {label} ({count})
                    </Chip>
                  );
                })}
            </div>
            {docLabelCounts.some(({ count }: any) => count === 0) && (
              <button
                onClick={() => setShowZeroDocLabels((prev) => !prev)}
                className="mt-3 flex items-center gap-2 text-sm text-moss-primary hover:text-moss-primary/80 transition-colors font-medium"
              >
                {showZeroDocLabels ? (
                  <>
                    <CaretUp size={16} />
                    <span>Verberg lege labels</span>
                  </>
                ) : (
                  <>
                    <CaretDown size={16} />
                    <span>Toon alle labels</span>
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* UniversalTable */}
        <UniversalTable
          {...FILES_TABLE_CONFIG}
          hiddenColumns={hiddenColumns}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          headerExtra={
            <div className="flex items-center gap-3">
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <ButtonNew
                    variant="secondaryNeutral"
                    icon={<CaretLeft size={14} />}
                    label="Vorige"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    isDisabled={currentPage === 1}
                  />
                  <span className="text-xs font-medium text-neutral-primary whitespace-nowrap">
                    {startItem}–{endItem} van {totalCount}
                  </span>
                  <ButtonNew
                    variant="secondaryNeutral"
                    label="Volgende"
                    icon={<CaretRight size={14} />}
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    isDisabled={currentPage === totalPages}
                  />
                </div>
              )}
              <ColumnVisibilityToggle
                columns={FILES_COLUMNS_CONFIG.columns}
                hiddenColumns={hiddenColumns}
                onHiddenColumnsChange={setHiddenColumns}
              />
            </div>
          }
          tableId="files-overview"
          rows={files}
          title="Documenten"
          description={totalCount > 0 ? `${startItem}–${endItem} van ${totalCount} documenten` : `${files.length} documenten gevonden`}
          groupBy="none"
          renderRow={renderFileRow}
          searchConfig={{
            searchPlaceholder: "Zoek in documenten...",
            filterSections,
            searchValue: searchText,
            onSearchChange: setSearchText,
            activeFilters: {
              building: buildingFilter,
              unit: unitFilter,
              status: statusFilter,
              ownership: ownershipFilter,
              label: labelFilter
            },
            onFilterChange: handleFilterChange,
            bulkActions: [
              {
                label: isDownloadingZip
                  ? "Downloaden..."
                  : selectAllMatching
                    ? `Download alle ${totalCount}`
                    : "Download selectie",
                icon: <DownloadSimple size={16} />,
                onClick: handleDownloadSelection,
                disabled: isDownloadingZip,
                variant: "blue",
              },
            ],
          }}
          onComplete={async () => {}}
          onUpdate={handleUpdateFile}
        />

        {isLoading && (
          <div className="flex justify-center items-center py-8">
            <div className="text-neutral-primary text-sm flex items-center gap-2">
              <div className="animate-spin h-5 w-5 border-2 border-moss-primary border-t-transparent rounded-full"></div>
              Documenten laden...
            </div>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onOpenChange={closeDeleteModal}
        isDismissable={true}
        isKeyboardDismissDisabled={true}
        className="font-normal text-neutral-primary"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                <p className="flex items-center gap-1 text-sm font-medium text-status-bad">
                  <AlertCircle className="h-[17px] w-[17px]" /> Verwijder bestand
                </p>
              </ModalHeader>
              <ModalBody>
                <p>
                  Wil je dit bestand verwijderen uit de Yallyhub? Zo ja, dan wordt
                  deze ook van SharePoint verwijderd.
                </p>
              </ModalBody>
              <ModalFooter className="justify-start">
                <ButtonNew
                  variant="primaryStatusRed"
                  onClick={confirmDelete}
                  icon={<Trash />}
                  label={"Verwijder bestand"}
                />
                <ButtonNew
                  variant="secondaryNeutral"
                  onClick={onClose}
                  label={"Nee, toch niet"}
                />
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* EPC Metadata Modal */}
      {isEpcModalOpen && currentEpcFile && (
        <EPCMetadataModal
          isOpen={isEpcModalOpen}
          onClose={() => {
            setIsEpcModalOpen(false);
            setCurrentEpcFile(null);
          }}
          fileLogId={Number(currentEpcFile.id)}
          currentEPC={currentEpc}
          onSuccess={handleEpcSaved}
          unitType={getEpcType()}
        />
      )}

      {/* Universal Picker Modal */}
      {pickerState.isOpen && pickerState.mode && (() => {
        const fieldMapping = FILES_TABLE_CONFIG.pickerConfig?.fieldMappings.find(
          mapping => mapping.eventMode === pickerState.mode
        );

        if (!fieldMapping) return null;

        const fieldLabel = FILES_TABLE_CONFIG.pickerConfig?.fieldLabels?.[fieldMapping.fieldName] || fieldMapping.fieldName;

        const isTextInput = fieldMapping.pickerMode === 'text-input';
        const baseConfig = isTextInput ? {
          defaultValue: pickerState.currentValue || '',
          searchPlaceholder: 'Voer samenvatting in...',
          minLength: 0,
          maxLength: 1000,
          required: false
        } : {
          defaultValue: pickerState.currentValue,
          allowRemoval: true
        };

        const modalConfig = {
          ...baseConfig,
          ...(fieldMapping.pickerMode === 'project-part' && {
            currentRenovationProjectId: pickerState.contextValue
          })
        };

        return (
          <UniversalPickerModal
            isOpen={pickerState.isOpen}
            mode={fieldMapping.pickerMode as any}
            title={
              <div className="flex flex-col gap-2">
                <span>{fieldLabel} instellen</span>
                <div className="flex flex-wrap gap-1.5">
                  {pickerState.fileIds.map((id) => (
                    <span
                      key={id}
                      className="inline-flex items-center px-2 py-0.5 rounded-md bg-moss-secondary-mid text-moss-primary text-xs font-medium"
                    >
                      Bestand {id}
                    </span>
                  ))}
                </div>
              </div>
            }
            onClose={handlePickerClose}
            onConfirm={handlePickerConfirm}
            config={modalConfig}
          />
        );
      })()}
    </>
  );
};

export default FilesOverview;
